import { FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import type { PairScore } from './suggestionsApi';

/**
 * Ocena pary zlecenie–pojazd 0–10 na ekranach rekomendacji (13.10.2026,
 * plan scoringu; przełącznik `pairScoring`, reguła w API:
 * `user-offers/pair-score.ts`).
 *
 * „Ocena” to liczba 0–10 dla całej pary (marża, wykonalność czasowa, dolot,
 * priorytet klienta) — co innego niż trzystopniowa „wykonalność” na karcie
 * pojazdu (`fleet/suggestionGrade.ts`), która przepisuje same werdykty
 * czasu. Kolejność z API zostaje po dolocie; „Sortuj po ocenie” to wybór
 * widoku, nie zmiana reguły. Bez dopracowywania kolorów (ustalenie 13.10).
 */

/** „7,4” — zawsze jedno miejsce po przecinku. */
export const scoreText = (n: number) =>
  n.toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Rachunek oceny zdaniami — do dymka. */
export function pairScoreTooltip(s: PairScore): string {
  const head =
    s.score === null
      ? `Ocena pary: brak — ${s.reason ?? 'brak danych'}.`
      : `Ocena pary ${scoreText(s.score)} na 10 (${s.coverage}).`;
  const parts = s.components
    .filter((c) => c.value !== null)
    .map(
      (c) =>
        `${c.label}: ${scoreText(c.value as number)} pkt, udział ${Math.round((c.share ?? 0) * 100)}% — ${c.text}.`,
    );
  const skipped = s.skipped.map((x) => `Pominięta składowa „${x.label.toLowerCase()}” — ${x.reason}.`);
  return [
    head,
    ...parts,
    ...skipped,
    'Ocena nie zmienia kolejności (ta zostaje po dolocie) i nikogo nie wyklucza.',
  ].join(' ');
}

/**
 * Linia „ocena 7,4 / 10” z rozbiciem w dymku. Bez oceny — „ocena —”
 * z powodem (brak danych to nie zero). Brak pola (przełącznik wyłączony,
 * starsze API) — nic.
 */
export const PairScoreLine = ({ score }: { score?: PairScore | null }) => {
  if (!score) return null;
  return (
    <Tooltip title={pairScoreTooltip(score)}>
      <Typography
        variant="caption"
        data-testid="pair-score"
        data-score={score.score ?? ''}
        sx={{ display: 'block', whiteSpace: 'nowrap', fontWeight: 600 }}
      >
        {score.score === null ? 'ocena —' : `ocena ${scoreText(score.score)} / 10`}
      </Typography>
    </Tooltip>
  );
};

/**
 * Wiersze po ocenie: najwyższa na górze, bez oceny na końcu; przy równej
 * ocenie kolejność z API (po dolocie) — sortowanie stabilne.
 */
export function byPairScore<T extends { pairScore?: PairScore | null }>(rows: readonly T[]): T[] {
  const value = (r: T) => r.pairScore?.score ?? null;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const va = value(a.row);
      const vb = value(b.row);
      if (va === null && vb === null) return a.index - b.index;
      if (va === null) return 1;
      if (vb === null) return -1;
      return vb - va || a.index - b.index;
    })
    .map((x) => x.row);
}

/** Przełącznik widoku „Sortuj po ocenie” — domyślnie wyłączony (kolejność z API). */
export const PairScoreSortToggle = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  // `describeChild`: dymek opisuje przełącznik, nazwę daje etykieta.
  <Tooltip
    describeChild
    title="Układa listę od najwyższej oceny pary 0–10. Domyślna kolejność to dolot — przełącznik zmienia tylko widok."
  >
    <FormControlLabel
      sx={{ whiteSpace: 'nowrap' }}
      control={<Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
      label="Sortuj po ocenie"
    />
  </Tooltip>
);
